import React, { useState } from 'react';
import { 
  TrashIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Crown Icon Component
const CrownIcon = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M5 16L3 8l2.5 2L12 4l6.5 6L21 8l-2 8H5zm2.5-7L6 8l1 4h10l1-4-1.5 1L12 7 7.5 9z"/>
  </svg>
);

// Charge Item Component
const ChargeItem = ({ charge, onDelete }) => {
  return (
    <div className="flex items-center justify-between bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors">
      <div className="flex-1">
        <div className="flex justify-between items-start mb-2">
          <span className="text-white font-semibold">${charge.fee}</span>
          <button
            onClick={() => onDelete(charge.id)}
            className="text-red-400 hover:text-red-300 transition-colors"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-1">
          <p className="text-gray-300 text-sm font-medium">{charge.violation}</p>
          <p className="text-gray-400 text-xs">{charge.description}</p>
        </div>
      </div>
      <button
        onClick={() => onDelete(charge.id)}
        className="ml-4 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
      >
        Delete
      </button>
    </div>
  );
};

// Add New Charge Form Component
const AddChargeForm = ({ onAdd, onCancel }) => {
  const [formData, setFormData] = useState({
    sectionNumber: '',
    violation: '',
    fee: ''
  });

  const handleSubmit = () => {
    if (formData.sectionNumber && formData.violation && formData.fee) {
      onAdd({
        id: Date.now(),
        sectionNumber: formData.sectionNumber,
        violation: formData.violation,
        fee: parseFloat(formData.fee),
        description: `Section ${formData.sectionNumber} violation`
      });
      setFormData({ sectionNumber: '', violation: '', fee: '' });
    }
  };

  const handleAddStandardCharges = () => {
    const standardCharges = [
      {
        id: Date.now() + 1,
        sectionNumber: '1001',
        violation: 'FAILURE TO APPEAR OR DAMAGE',
        fee: 2150.75,
        description: 'Standard failure to appear violation'
      },
      {
        id: Date.now() + 2,
        sectionNumber: '1002',
        violation: 'FAILURE TO COMPLY',
        fee: 2250.75,
        description: 'Standard failure to comply violation'
      },
      {
        id: Date.now() + 3,
        sectionNumber: '1003',
        violation: 'CONTEMPT OF COURT',
        fee: 1867.90,
        description: 'Contempt of court violation'
      },
      {
        id: Date.now() + 4,
        sectionNumber: '1004',
        violation: 'INTERFERING WITH JUDICIAL PROCEEDINGS',
        fee: 1867.90,
        description: 'Interfering with judicial proceedings'
      }
    ];

    standardCharges.forEach(charge => onAdd(charge));
  };

  return (
    <div className="space-y-4">
      <div>
        <input
          type="text"
          placeholder="Section Number"
          value={formData.sectionNumber}
          onChange={(e) => setFormData({ ...formData, sectionNumber: e.target.value })}
          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      <div>
        <input
          type="text"
          placeholder="Violation"
          value={formData.violation}
          onChange={(e) => setFormData({ ...formData, violation: e.target.value })}
          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      <div>
        <input
          type="number"
          step="0.01"
          placeholder="Fee"
          value={formData.fee}
          onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      <div className="flex space-x-3">
        <button
          onClick={handleSubmit}
          className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-black font-semibold py-2 px-4 rounded-lg transition-all duration-200"
        >
          Add Charge
        </button>
        <button
          onClick={handleAddStandardCharges}
          className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-black font-semibold py-2 px-4 rounded-lg transition-all duration-200"
        >
          Add Standard Charges
        </button>
      </div>

      {onCancel && (
        <button
          onClick={onCancel}
          className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
        >
          Cancel
        </button>
      )}
    </div>
  );
};

// Federal Bonding Costs Component
const FederalBondingCosts = () => {
  const [activeTab, setActiveTab] = useState('immigration');

  const tabs = [
    { id: 'immigration', name: 'Immigration' },
    { id: 'federal', name: 'Federal' },
    { id: 'appeals', name: 'Appeals' },
    { id: 'civil', name: 'Civil Rights' }
  ];

  const bondingInfo = {
    immigration: {
      title: 'Immigration Bonds',
      description: 'Federal immigration bonding costs and procedures',
      details: [
        'Delivery bonds typically range from $1,500 to $10,000',
        'Departure bonds can be $500 to $10,000 or more',
        'Bond amounts depend on flight risk and community ties',
        'Payment accepted via cashier\'s check or money order'
      ]
    },
    federal: {
      title: 'Federal Criminal Bonds', 
      description: 'Federal criminal case bonding information',
      details: [
        'Federal bonds often higher than state bonds',
        'Magistrate judge sets initial bond amount',
        'Pretrial services report influences decision',
        'May require property or third-party custody'
      ]
    },
    appeals: {
      title: 'Appeal Bonds',
      description: 'Bonding during the appeals process',
      details: [
        'Appeal bonds preserve right to appeal',
        'Amount typically 110% of judgment plus costs',
        'Required to stay execution of judgment',
        'Court may waive or reduce in hardship cases'
      ]
    },
    civil: {
      title: 'Civil Rights Bonds',
      description: 'Bonds related to civil rights cases',
      details: [
        'Injunction bonds protect against wrongful restraint',
        'Typically required in civil rights litigation',
        'Amount varies based on potential damages',
        'May be waived for qualifying organizations'
      ]
    }
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-2 bg-gray-700 rounded-lg p-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-purple-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-gray-600'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-white mb-2">
          {bondingInfo[activeTab].title}
        </h3>
        <p className="text-gray-400 mb-4">
          {bondingInfo[activeTab].description}
        </p>
        
        <ul className="space-y-2">
          {bondingInfo[activeTab].details.map((detail, index) => (
            <li key={index} className="flex items-start space-x-2">
              <span className="text-yellow-500 mt-1">•</span>
              <span className="text-gray-300">{detail}</span>
            </li>
          ))}
        </ul>

        {/* Upload Section for each tab */}
        <div className="mt-6 pt-6 border-t border-gray-700">
          <h4 className="text-lg font-semibold text-white mb-3">
            Upload {bondingInfo[activeTab].title} Documents
          </h4>
          <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center">
            <input
              type="file"
              multiple
              className="hidden"
              id={`upload-${activeTab}`}
            />
            <label
              htmlFor={`upload-${activeTab}`}
              className="cursor-pointer flex flex-col items-center space-y-2"
            >
              <PlusIcon className="h-8 w-8 text-gray-400" />
              <span className="text-gray-400">Upload documents for {bondingInfo[activeTab].title.toLowerCase()}</span>
              <span className="text-sm text-gray-500">Click to select files</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};




import AdminLayoutWrapper from './common/AdminLayoutWrapper';
// Main Content Area
const MainContent = ({ children }) => {
  return (
    <AdminLayoutWrapper>
        {children}
    </AdminLayoutWrapper>
  );
};


// Main Charges Dashboard Component
const ChargesComponent = () => {
  const [charges, setCharges] = useState([
    {
      id: 1,
      sectionNumber: '1001',
      violation: 'FAILURE TO APPEAR OR DAMAGE',
      fee: 2150.75,
      description: 'FAILURE TO APPEAR OR DAMAGE'
    },
    {
      id: 2,
      sectionNumber: '1002', 
      violation: 'FAILURE TO COMPLY',
      fee: 2250.75,
      description: 'FAILURE TO COMPLY'
    },
    {
      id: 3,
      sectionNumber: '1003',
      violation: 'CONTEMPT OF COURT',
      fee: 1867.90,
      description: 'CONTEMPT OF COURT'
    },
    {
      id: 4,
      sectionNumber: '1004',
      violation: 'INTERFERING WITH JUDICIAL PROCEEDINGS', 
      fee: 1867.90,
      description: 'INTERFERING WITH JUDICIAL PROCEEDINGS'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeSection, setActiveSection] = useState('charges'); // 'charges' or 'bonding'

  const handleAddCharge = (newCharge) => {
    setCharges([...charges, newCharge]);
    setShowAddForm(false);
  };

  const handleDeleteCharge = (chargeId) => {
    setCharges(charges.filter(charge => charge.id !== chargeId));
  };

  const filteredCharges = charges.filter(charge =>
    charge.violation.toLowerCase().includes(searchTerm.toLowerCase()) ||
    charge.sectionNumber.includes(searchTerm) ||
    charge.fee.toString().includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-gray-900 to-black p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <CrownIcon size={32} className="text-yellow-500" />
            <h1 className="text-2xl font-bold text-white">William Walker</h1>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-4 mb-8">
          <button
            onClick={() => setActiveSection('charges')}
            className={`px-6 py-2 rounded-lg font-semibold transition-all duration-200 ${
              activeSection === 'charges'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Duck Charges
          </button>
          <button
            onClick={() => setActiveSection('bonding')}
            className={`px-6 py-2 rounded-lg font-semibold transition-all duration-200 ${
              activeSection === 'bonding'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Federal Bonding Costs
          </button>
        </div>

        {/* Charges Section */}
        {activeSection === 'charges' && (
          <div className="space-y-6">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Charges Dashboard</h2>

              {/* Search */}
              <div className="mb-6">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search charges..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Current Charges */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">Current Charges</h3>
                  <span className="text-gray-400">({filteredCharges.length} charges)</span>
                </div>

                {filteredCharges.length === 0 ? (
                  <div className="text-center py-8">
                    <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-400">No charges found matching your search</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCharges.map((charge) => (
                      <ChargeItem
                        key={charge.id}
                        charge={charge}
                        onDelete={handleDeleteCharge}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Add New Charge Section */}
              <div className="border-t border-gray-700 pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">Add a New Charge</h3>
                  {!showAddForm && (
                    <button
                      onClick={() => setShowAddForm(true)}
                      className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold px-4 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span>Add Charge</span>
                    </button>
                  )}
                </div>

                {showAddForm ? (
                  <AddChargeForm
                    onAdd={handleAddCharge}
                    onCancel={() => setShowAddForm(false)}
                  />
                ) : (
                  <p className="text-gray-400">Click "Add Charge" to create a new charge entry</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Federal Bonding Section */}
        {activeSection === 'bonding' && <FederalBondingCosts />}
      </div>
    </div>
  );
};

const ChargesDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-900">
        <MainContent>
          <ChargesComponent />
        </MainContent>
      
    </div>
  );
};


export default ChargesDashboard;